import { useFocusEffect } from "expo-router";
import { useCallback, type RefObject } from "react";
import {
  AccessibilityInfo,
  findNodeHandle,
  Platform,
  type View,
} from "react-native";

import Logging from "$src/core/services/logging/logging";

const DEFAULT_FOCUS_DELAY_MS = 500;

export type AccessibilityAnnouncementApi = {
  announce: (message: string) => void;
  delay: (delayMs: number) => Promise<void>;
  focusAfter: (ref: RefObject<View | null>, delayMs?: number) => void;
};

type Options = {
  enabled?: boolean;
};

const useAccessibilityAnnouncement = (
  onAnnounce: (api: AccessibilityAnnouncementApi) => void | Promise<void>,
  { enabled = true }: Options = {},
) => {
  /* Run accessibility announcements when the screen is focused and a screen reader is on. */
  useFocusEffect(
    useCallback(() => {
      if (!enabled) return undefined;

      const timeoutIds = new Set<ReturnType<typeof setTimeout>>();
      let isCancelled = false;

      const api: AccessibilityAnnouncementApi = {
        announce: (message) => {
          AccessibilityInfo.announceForAccessibility(message);
        },
        delay: (delayMs) =>
          new Promise<void>((resolve) => {
            const timeoutId = setTimeout(() => {
              if (isCancelled) return;

              resolve();
            }, delayMs);
            timeoutIds.add(timeoutId);
          }),
        focusAfter: (ref, delayMs = DEFAULT_FOCUS_DELAY_MS) => {
          const timeoutId = setTimeout(() => {
            if (isCancelled) return;

            if (Platform.OS === "web") {
              (ref.current as { focus?: () => void } | null)?.focus?.();
              return;
            }

            const handle = findNodeHandle(ref.current);
            if (handle) {
              AccessibilityInfo.setAccessibilityFocus(handle);
            }
          }, delayMs);
          timeoutIds.add(timeoutId);
        },
      };

      AccessibilityInfo.isScreenReaderEnabled()
        .then((isScreenReaderEnabled) => {
          if (!isScreenReaderEnabled || isCancelled) return;

          Promise.resolve(onAnnounce(api)).catch((error) => {
            Logging.error("Failed to run accessibility announcement", {
              error,
            });
          });
        })
        .catch((error) => {
          Logging.error("Failed to check screen reader status", { error });
        });

      return () => {
        isCancelled = true;
        timeoutIds.forEach((timeoutId) => {
          clearTimeout(timeoutId);
        });
        timeoutIds.clear();
      };
    }, [enabled, onAnnounce]),
  );
};

export default useAccessibilityAnnouncement;
